/*
 * fn: parseRecResponse
 *
 * Parses the Rec API response (CardView) to the Rec object
 *
 */
import { type CardCardStatsType } from '@/defs/apiLegacy/pagePartsResponse'
import { type RecRoleType, type RecType } from '@/defs/rec'
import { buildCardId, getCardRef, isCardPrinting } from '@/utils/cardUtil'
import { slugify } from '@/utils/commonUtil'
import { buildRec } from '@/utils/recUtil'

const SALT_REGEX = /Salt Score: ([\d.]+)/

type Props = {
  category?: RecType['category']
  count?: RecType['count']
  defaultOrder?: RecType['defaultOrder']
  partnerCollectorNumber?: string
  partnerName?: string
  partnerSet?: string
  role?: RecRoleType
} & CardCardStatsType

export const parseRecResponse = ({
  category,
  count,
  defaultOrder,
  partnerCollectorNumber,
  partnerSet,
  role,
  ...props
}: Props): RecType => {
  let saltRec = props.salt
  if (saltRec === undefined) {
    const match = props.label?.match(SALT_REGEX)?.[1]
    saltRec = match ? Number(match) : undefined
    if (saltRec !== undefined && isNaN(saltRec)) saltRec = undefined
  }

  const name = props.cards?.[0]?.name ?? props.name ?? 'Card Name'
  const numDecks = props.num_decks
  const potentialDecks = props.potential_decks
  const unpopularity = props.unpopularity
  const partnerName = props.partnerName || (props.is_partner ? props.cards?.[1]?.name : undefined)

  const inclusion =
    numDecks !== undefined && potentialDecks !== undefined && Number.isFinite(numDecks / potentialDecks)
      ? numDecks / potentialDecks
      : unpopularity !== undefined
        ? 1 - unpopularity
        : props.inclusion

  const slug = !partnerName ? props.sanitized || slugify(name) : slugify(name)

  const cardId = buildCardId({ collectorNumber: props.collector_number, set: props.set, slug })
  const partnerRef = partnerName
    ? getCardRef({ collectorNumber: partnerCollectorNumber, name: partnerName, set: partnerSet })
    : undefined

  return buildRec({
    cardId,
    category: category ? slugify(category) : undefined,
    count,
    defaultOrder,
    inclusion,
    lift: props.lift,
    name,
    numDecks,
    partnerRef,
    potentialDecks,
    rank: props.rank,
    role,
    saltRec,
    scryfallId: isCardPrinting(cardId) ? undefined : props.id,
    synergy: props.synergy,
    trendZscore: props.trend_zscore,
  })
}
